import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateBox } from "devextreme-react/date-box";
import PivotGrid, { FieldChooser, FieldPanel } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import "./PositionsPositions.css";
import { HeaderFilter } from "devextreme-react/data-grid";
import { Search } from "devextreme-react/gantt";
import jsPDF from "jspdf";
import "jspdf-autotable";

import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import { exportPivotGrid } from "devextreme/excel_exporter";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";
import { toDate, fmt, fmtUK } from "../../utils/dateUtils";

interface PivotField {
  caption?: string;
  dataField?: string;
  name?: string;
  area?: string;
  filterValues?: unknown[];
  filterType?: string;
  searchValue?: string;
  summaryType?: string;
  format?: string | Record<string, unknown>;
  dataType?: string;
}

interface FlatRow {
  node: any;
  level: number;
}

interface ColLeaf {
  node: any;
  path: string[];
}

function formatFilters(fields: PivotField[]): string[] {
  const items = (fields || [])
    .filter(
      (f) =>
        f &&
        f.area !== "data" &&
        ((Array.isArray(f.filterValues) && f.filterValues.length > 0) ||
          (typeof f.searchValue === "string" && f.searchValue.length > 0))
    )
    .map((f) => {
      const cap = f.caption || f.dataField || f.name || "Field";
      const op = (f.filterType || "include") === "exclude" ? "≠" : "=";
      const vals = (f.filterValues || []).map((v) =>
        Array.isArray(v) ? v.join(" / ") : String(v)
      );
      return f.searchValue
        ? `${cap} ${op} ${vals.join(", ")} (search: "${f.searchValue}")`
        : `${cap} ${op} ${vals.join(", ")}`;
    });

  return items;
}

const PositionsPivotTable: React.FC = () => {
  const pivotRef = useRef<any>(null);

  const [cobDate, setCobDate] = useState<Date | null>(null);
  const [, , , latestCobDate] = useCobDate("/positions_latest_cob");
  const [compareDate, setCompareDate] = useState<Date | null>(null);

  const [positionData, setPositionData] = useState<any[]>([]);
  const [compareData, setCompareData] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string>("");

  const [mode, setMode] = useState<string>("current");
  const [searchEnabled, setSearchEnabled] = useState<boolean>(true);
  const [showRelevantValues, setShowRelevantValues] = useState<boolean>(true);

  const prevCob = useRef<Date | null>(null);
  const prevCmp = useRef<Date | null>(null);

  useEffect(() => {
    if (cobDate !== null) return;

    const ac = new AbortController();
    setLoading(true);
    setErr("");

    (async () => {
      try {
        const r = await fetch(`${baseUrl}/positions_exposure`, { signal: ac.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { data, cob_date } = await r.json();

        setCobDate(toDate(cob_date));
        setPositionData(data || []);
        setCompareDate(null);
      } catch (e: any) {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [cobDate]);

  useEffect(() => {
    if (!cobDate) return;

    if (!prevCob.current || cobDate.getTime() !== prevCob.current.getTime()) {
      setMode("current");
      prevCob.current = cobDate;
    } else if (compareDate && (!prevCmp.current || compareDate.getTime() !== prevCmp.current.getTime())) {
      setMode("diff");
      prevCmp.current = compareDate;
    }
  }, [cobDate, compareDate]);

  const didInit = useRef<boolean>(false);

  useEffect(() => {
    if (cobDate == null) return;

    if (!didInit.current) {
      didInit.current = true;
      document.body.style.cursor = "";
      return;
    }

    const ac = new AbortController();
    document.body.style.cursor = "wait";
    setLoading(true);
    setErr("");

    (async () => {
      try {
        setCompareDate(null);
        const qs = new URLSearchParams({
          cob_date: fmt(cobDate) as string,
        });

        const r = await fetch(`${baseUrl}/positions_exposure?${qs.toString()}`, {
          signal: ac.signal,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { data } = await r.json();
        setPositionData(data || []);
      } catch (e: any) {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
        document.body.style.cursor = "";
      }
    })();

    return () => {
      ac.abort();
      document.body.style.cursor = "";
    };
  }, [cobDate]);

  const didMount = useRef<boolean>(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (!compareDate || compareDate === cobDate) return;

    const ac = new AbortController();
    document.body.style.cursor = "wait";
    setLoading(true);
    setErr("");

    const qs = new URLSearchParams({
      cob_date_start: fmt(cobDate) as string,
      cob_date_end: fmt(compareDate) as string,
    });

    fetch(`${baseUrl}/positions_exposure_diff?${qs.toString()}`, { signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(({ data }: { data: any[] }) => setCompareData(data || []))
      .catch((e: any) => {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      })
      .finally(() => {
        setLoading(false);
        document.body.style.cursor = "";
      });

    return () => {
      ac.abort();
      document.body.style.cursor = "";
    };
  }, [compareDate, cobDate]);

  const activeStore = mode === "current" ? positionData : compareData;

  const fetchStatusText = useMemo(() => {
    if (loading) return "Loading…";
    if (err) return err;
    const n = Array.isArray(activeStore) ? activeStore.length : 0;
    return `Loaded (${n.toLocaleString()} trades)`;
  }, [loading, err, activeStore]);

  const fields = useMemo(
    () => [
      { caption: "Year", dataField: "year", area: "row" },
      { caption: "Quarter", dataField: "quarter", area: "row" },
      { caption: "Month", dataField: "period", area: "row" },

      { caption: "RawTrades", dataField: "raw_trades", area: "filter" },
      { caption: "Class", dataField: "class", area: "filter" },
      { caption: "Book", dataField: "book", area: "filter" },
      { caption: "Owner", dataField: "owner", area: "filter" },
      { caption: "ProductType", dataField: "product_type", area: "filter" },

      { caption: "RiskPod", dataField: "risk_pod", area: "filter" },
      { caption: "RiskSubPod", dataField: "risk_sub_pod", area: "filter" },

      { caption: "AssetType", dataField: "assettype", area: "column" },
      { caption: "AssetCategory", dataField: "assetcategory", area: "column" },
      { caption: "AssetClass", dataField: "assetclass", area: "column" },
      { caption: "Underlying", dataField: "underlying", area: "column" },
      { caption: "Put/Call", dataField: "put_call", area: "column" },
      { caption: "Strike", dataField: "strike_price", area: "column" },
      { caption: "Gamma", dataField: "gamma", area: "column" },
      { caption: "Vega", dataField: "vega", area: "column" },
      { caption: "Theta", dataField: "theta", area: "column" },

      {
        caption: "Sum of Notional (EUR)",
        dataField: "notional_eur",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: "Sum of Delta",
        dataField: "delta",
        summaryType: "sum",
        format: "fixedPoint",
      },
    ],
    []
  );

  const dataSource = useMemo(
    () => {
      const mappedFields: any[] = fields.map((f) => {
        if (f.dataField === "notional_eur" || f.dataField === "delta") {
          return {
            ...f,
            dataType: "number" as const,
            format: { type: "fixedPoint", precision: 0 },
            customizeText: (cellInfo: any) => {
              const { value } = cellInfo;
              const formatted = cellInfo.text ?? cellInfo.valueText ?? "";
              if (!Number.isFinite(value)) return formatted;
              if (value < 0) {
                const absFmt = Math.abs(value).toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                });
                return `(${absFmt})`;
              }
              return formatted;
            },
          };
        }

        if (f.dataField === "class") {
          return {
            ...f,
            area: f.area ?? "filter",
            filterType: "include" as const,
            filterValues: ["RMS"],
          };
        }

        return f;
      });
      return new PivotGridDataSource({
        fields: mappedFields,
        store: activeStore || [],
      });
    },
    [fields, activeStore]
  );

  const onExport = async (): Promise<void> => {
    if (!pivotRef.current) return;

    const pivotInstance = pivotRef.current.instance();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Pivot");

    await exportPivotGrid({
      component: pivotInstance,
      worksheet,
      customizeCell: ({ pivotCell, excelCell }: any) => {
        if (!pivotCell) return;

        if (
          pivotCell.area === "row" ||
          pivotCell.area === "column" ||
          pivotCell.area === "filter" ||
          (pivotCell.area === "data" &&
            pivotCell.rowType === "D" &&
            pivotCell.columnType === "D" &&
            pivotCell.type === "D")
        ) {
          excelCell.font = { ...(excelCell.font || {}), bold: true };
        }

        if (pivotCell.area === "data" && pivotCell.value != null) {
          const numericValue = Number(pivotCell.value);
          if (!Number.isNaN(numericValue)) {
            excelCell.value = numericValue;
            excelCell.numFmt = "#,##0.00;[Red]-#,##0.00";
            if (numericValue < 0) {
              excelCell.font = { ...(excelCell.font || {}), color: { argb: "FFFF0000" } };
            }
          }
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "positions.xlsx");
  };

  const flattenRows = (nodes: any[], level: number = 0, out: FlatRow[] = []): FlatRow[] => {
    (nodes || []).forEach((n: any) => {
      out.push({ node: n, level });
      if (n.children?.length) flattenRows(n.children, level + 1, out);
    });
    return out;
  };

  const leafColumnPaths = (nodes: any[], path: string[] = [], out: ColLeaf[] = []): ColLeaf[] => {
    (nodes || []).forEach((n: any) => {
      const next = [...path, (n.text ?? n.value ?? "")].filter(Boolean);
      if (n.children?.length) leafColumnPaths(n.children, next, out);
      else out.push({ node: n, path: next });
    });
    return out;
  };

  const cellValue = (values: any, r: number, c: number): string | number => {
    const cellArr = values?.[r]?.[c];
    const raw = Array.isArray(cellArr) ? cellArr[0] : cellArr;
    const v = raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
    if (v == null) return "";
    const num = Number(v);
    return Number.isNaN(num) ? v : num;
  };

  const onExportPdf = async (): Promise<void> => {
    const pivot = pivotRef.current.instance();
    const ds = pivot.getDataSource();

    await ds.load();

    if (ds.expandAll) {
      ds.expandAll("row");
      ds.expandAll("column");
      await ds.load();
    }

    const data = ds.getData();
    const rowsFlat = flattenRows(data.rows);
    const colLeaves = leafColumnPaths(data.columns);

    const rowFields = ds.getAreaFields("row");
    const headRowLabel = rowFields?.[0]?.caption ?? "Row";

    const head = [
      [
        headRowLabel,
        ...(colLeaves.length ? colLeaves.map((c) => c.path.join(" / ")) : ["Value"]),
      ],
    ];

    const body = rowsFlat.map((r, rowIndex) => {
      const label = `${"  ".repeat(r.level)}${r.node.text ?? r.node.value ?? ""}`;
      if (!colLeaves.length) return [label, cellValue(data.values, rowIndex, 0)];
      return [label, ...colLeaves.map((_: ColLeaf, colIndex: number) => cellValue(data.values, rowIndex, colIndex))];
    });

    const doc = new jsPDF("landscape", "pt", "a4");
    const d = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`Positions ${d}`, 20, 25);

    (doc as any).autoTable({
      head,
      body,
      styles: { fontSize: 9 },
      headStyles: { fontStyle: "bold", fillColor: [230, 230, 230], textColor: [0, 0, 0] },
      didParseCell: ({ section, cell, column }: any) => {
        if (section === "body" && column.index > 0 && typeof cell.raw === "number" && cell.raw < 0) {
          cell.styles.textColor = [255, 0, 0];
          cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 40, left: 20, right: 20, bottom: 20 },
    });

    doc.save("pivot.pdf");
  };

  const onCellPrepared = (e: any): void => {
    const v = e.cell?.value;
    e.cellElement.classList.toggle("neg", Number.isFinite(v) && v < 0);
  };

  const [filterChips, setFilterChips] = useState<string[]>([]);
  const lastSig = useRef<string>("");

  useEffect(() => {
    const ds = dataSource;
    if (!ds) return;

    const extract = () => {
      const chips = formatFilters(ds.fields() as PivotField[]);
      const sig = JSON.stringify(chips);
      if (sig !== lastSig.current) {
        lastSig.current = sig;
        setFilterChips(chips);
      }
    };

    extract();
    ds.on("changed", extract);

    return () => {
      try {
        ds.off("changed", extract);
      } catch {}
    };
  }, [dataSource]);

  return (
    <div className="dashboard-container">
      <div className="filters-row">
        <div className="filter-block">
          <div className="filters-group">
            <label>CoB:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={cobDate}
              onValueChanged={(e: any) => setCobDate(e.value)}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>

          <div className="filters-group">
            <label>Comparison:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={compareDate}
              onValueChanged={(e: any) => setCompareDate(e.value)}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>
        </div>

        <div className="filter-block">
          <div className="filters-group" style={{ marginTop: 8 }}>
            <div
              style={{
                fontSize: 12,
                opacity: err ? 1 : 0.75,
                color: err ? "crimson" : "inherit",
              }}
            >
              {fetchStatusText}
            </div>

            {mode === "current" && cobDate ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>COB: {fmtUK(cobDate)}</div>
            ) : null}

            {mode === "diff" && cobDate && compareDate ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Δ {fmtUK(cobDate)} → {fmtUK(compareDate)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="filter-summary">
        {filterChips.length ? (
          filterChips.map((txt, i) => (
            <span key={i} className="chip">
              {txt}
            </span>
          ))
        ) : (
          <em>No filters applied</em>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <button
          onClick={onExport}
          style={{
            padding: "8px 16px",
            borderRadius: "4px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontWeight: "bold",
            letterSpacing: "0.5px",
          }}
        >
          EXPORT EXCEL
        </button>
        <button
          onClick={onExportPdf}
          style={{
            padding: "8px 16px",
            borderRadius: "4px",
            backgroundColor: "#d9534f",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontWeight: "bold",
            letterSpacing: "0.5px",
          }}
        >
          EXPORT PDF
        </button>
      </div>

      <div className="positions-pivot-section">
        <div className="positions-pivot-scroll">
          <PivotGrid
            ref={pivotRef}
            id="pivotgrid"
            dataSource={dataSource}
            allowFiltering={true}
            allowSorting={true}
            allowSortingBySummary={true}
            showBorders={true}
            // @ts-ignore
            showDataFields
            dataFieldArea="row"
            showRowFields
            showColumnFields
            height="70vh"
            width="100%"
            onCellPrepared={onCellPrepared}
          >
            {/* @ts-ignore */}
            <HeaderFilter showRelevantValues={showRelevantValues} width={300} height={400}>
              <Search enabled={searchEnabled} />
            </HeaderFilter>
            <FieldChooser allowSearch={true} />
            <FieldPanel visible={true} />
          </PivotGrid>
        </div>
      </div>

    </div>
  );
};

export default PositionsPivotTable;
